import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  Chip,
  Stack,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Collapse,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputAdornment,
  ClickAwayListener,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterList as FilterIcon,
  Tune as TuneIcon,
  LocalOffer as TagIcon,
  Person as PersonIcon,
  Sort as SortIcon,
} from '@mui/icons-material';
import { useTheme, alpha } from '@mui/material/styles';
import { useSearch } from '../contexts/SearchContext';
import { 
  getAllTagsWithCounts, 
  getAuthorsWithCounts, 
  getSearchSuggestions 
} from '../utils/searchUtils';
import { useNavigate } from 'react-router-dom';

const SearchComponent = ({ 
  variant = 'default', // 'default', 'compact', 'full'
  showFilters = true,
  autoFocus = false,
  placeholder = 'Search blog posts...',
  onSearchSubmit = null,
  onBlur = null,
  onInputChange = null,
  enableSuggestions = true
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const searchInputRef = useRef();
  const suggestionsRef = useRef();
  
  const {
    searchQuery,
    selectedTags,
    selectedAuthor,
    sortBy,
    setSearchQuery,
    setSelectedTags,
    setSelectedAuthor,
    setSortBy,
    removeTag,
    clearAllFilters,
    hasActiveFilters,
  } = useSearch();

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [localQuery, setLocalQuery] = useState(searchQuery);

  const allTags = getAllTagsWithCounts();
  const allAuthors = getAuthorsWithCounts();

  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  useEffect(() => {
    if (autoFocus && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [autoFocus]);

  // Handle search suggestions
  useEffect(() => {
    if (!enableSuggestions) {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(() => {
      if (localQuery.trim().length > 1) {
        const newSuggestions = getSearchSuggestions(localQuery, 8);
        setSuggestions(newSuggestions);
      } else {
        setSuggestions([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localQuery, enableSuggestions]);

  const handleSearchChange = (value) => {
    setLocalQuery(value);
    if (onInputChange) {
      onInputChange(value);
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(localQuery);
    setShowSuggestions(false);
    
    if (onSearchSubmit) {
      onSearchSubmit(localQuery);
    } else {
      // Navigate to search results page
      navigate('/blog/search');
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearchSubmit();
    } else if (event.key === 'Escape') {
      setShowSuggestions(false);
      if (onBlur) onBlur(event);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setLocalQuery(suggestion);
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    
    if (onSearchSubmit) {
      onSearchSubmit(suggestion);
    } else {
      navigate('/blog/search');
    }
  };


  const handleClearSearch = () => {
    setLocalQuery('');
    setSearchQuery('');
    setSuggestions([]);
    setShowSuggestions(false);
    if (onInputChange) {
      onInputChange('');
    }
  };

  const handleToggleFilters = () => {
    setShowAdvancedFilters(!showAdvancedFilters);
  };

  if (variant === 'compact') {
    return (
      <Box sx={{ position: 'relative', width: '100%', maxWidth: 400 }}>
        <TextField
          ref={searchInputRef}
          fullWidth
          size="small"
          placeholder={placeholder}
          value={localQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          onKeyPress={handleKeyPress}
          onFocus={() => enableSuggestions && setShowSuggestions(true)}
          onBlur={(e) => {
            // Delay hiding suggestions to allow clicks
            setTimeout(() => setShowSuggestions(false), 150);
            if (onBlur) onBlur(e);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
            endAdornment: localQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleClearSearch}>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.palette.mode === 'dark' 
                ? alpha(theme.palette.common.white, 0.05)
                : alpha(theme.palette.common.black, 0.04),
            }
          }}
        />
        
        {/* Suggestions Dropdown */}
        {enableSuggestions && showSuggestions && suggestions.length > 0 && (
          <Paper
            ref={suggestionsRef}
            elevation={8}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1400,
              mt: 1,
              maxHeight: 300,
              overflow: 'auto',
            }}
          >
            <List dense>
              {suggestions.map((suggestion, index) => (
                <ListItem key={index} disablePadding>
                  <ListItemButton onClick={() => handleSuggestionClick(suggestion)}>
                    <ListItemText 
                      primary={suggestion}
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Box>
    );
  }

  return (
    <ClickAwayListener onClickAway={() => setShowSuggestions(false)}>
      <Box sx={{ width: '100%', position: 'relative' }}>
        {/* Main Search Input */}
        <Paper 
          elevation={showSuggestions ? 4 : 1}
          sx={{ 
            p: 2,
            border: '1px solid',
            borderColor: showSuggestions ? 'primary.main' : 'divider',
            borderRadius: 3,
            transition: 'all 0.2s ease',
          }}
        >
          <Stack spacing={2}>
            {/* Search Field */}
            <Box sx={{ position: 'relative' }}>
              <TextField
                ref={searchInputRef}
                fullWidth
                placeholder={placeholder}
                value={localQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyPress={handleKeyPress}
                onFocus={() => enableSuggestions && setShowSuggestions(true)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'primary.main', fontSize: 24 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <Stack direction="row" spacing={1}>
                        {localQuery && (
                          <IconButton size="small" onClick={handleClearSearch}>
                            <ClearIcon />
                          </IconButton>
                        )}
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleSearchSubmit}
                          sx={{ minWidth: 'auto', px: 2 }}
                        >
                          Search
                        </Button>
                      </Stack>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: '1.1rem',
                    '& fieldset': {
                      border: 'none',
                    },
                  },
                }}
              />
            </Box>

            {/* Active Filters Display */}
            {hasActiveFilters() && (
              <Box>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                  {selectedTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => removeTag(tag)}
                      color="primary"
                      variant="outlined"
                      size="small"
                      icon={<TagIcon />}
                    />
                  ))}
                  {selectedAuthor && (
                    <Chip
                      label={`Author: ${allAuthors.find(a => a.id === selectedAuthor)?.name || selectedAuthor}`}
                      onDelete={() => setSelectedAuthor('')}
                      color="secondary"
                      variant="outlined"
                      size="small"
                      icon={<PersonIcon />}
                    />
                  )}
                  {sortBy !== 'date-desc' && (
                    <Chip
                      label={`Sort: ${sortBy.replace('-', ' ')}`}
                      onDelete={() => setSortBy('date-desc')}
                      color="default"
                      variant="outlined"
                      size="small"
                      icon={<SortIcon />}
                    />
                  )}
                  <Button
                    size="small"
                    onClick={clearAllFilters}
                    startIcon={<ClearIcon />}
                    sx={{ minHeight: 24 }}
                  >
                    Clear All
                  </Button>
                </Stack>
              </Box>
            )}

            {/* Filter Toggle */}
            {showFilters && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Button
                  startIcon={<FilterIcon />}
                  onClick={handleToggleFilters}
                  size="small"
                  variant={showAdvancedFilters ? 'contained' : 'outlined'}
                >
                  Filters
                </Button>
                <Typography variant="caption" color="text.secondary">
                  {hasActiveFilters() ? 'Filters active' : 'No filters applied'}
                </Typography>
              </Box>
            )}

            {/* Advanced Filters */}
            <Collapse in={showAdvancedFilters}>
              <Stack spacing={3} sx={{ pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TuneIcon />
                  Advanced Filters
                </Typography>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  {/* Tags Filter */}
                  <Autocomplete
                    multiple
                    options={allTags}
                    getOptionLabel={(option) => option.tag}
                    value={allTags.filter(tag => selectedTags.includes(tag.tag))}
                    onChange={(event, newValue) => {
                      setSelectedTags(newValue.map(item => item.tag));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Tags"
                        placeholder="Select tags..."
                        size="small"
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                          <span>{option.tag}</span>
                          <Chip label={option.count} size="small" variant="outlined" />
                        </Box>
                      </li>
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option.tag}
                          {...getTagProps({ index })}
                          size="small"
                          color="primary"
                        />
                      ))
                    }
                    sx={{ minWidth: 200, flexGrow: 1 }}
                  />

                  {/* Author Filter */}
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Author</InputLabel>
                    <Select
                      value={selectedAuthor}
                      onChange={(e) => setSelectedAuthor(e.target.value)}
                      label="Author"
                    >
                      <MenuItem value="">All Authors</MenuItem>
                      {allAuthors.map((author) => (
                        <MenuItem key={author.id} value={author.id}>
                          {author.name} ({author.postCount})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Sort Filter */}
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Sort By</InputLabel>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      label="Sort By"
                    >
                      <MenuItem value="date-desc">Latest First</MenuItem>
                      <MenuItem value="date-asc">Oldest First</MenuItem>
                      <MenuItem value="title-asc">Title A-Z</MenuItem>
                      <MenuItem value="title-desc">Title Z-A</MenuItem>
                      <MenuItem value="relevance">Most Relevant</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>
            </Collapse>
          </Stack>
        </Paper>

        {/* Search Suggestions */}
        {enableSuggestions && showSuggestions && suggestions.length > 0 && (
          <Paper
            elevation={8}
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 1400,
              mt: 1,
              maxHeight: 400,
              overflow: 'auto',
            }}
          >
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Suggestions
              </Typography>
              <List dense>
                {suggestions.map((suggestion, index) => (
                  <ListItem key={index} disablePadding>
                    <ListItemButton 
                      onClick={() => handleSuggestionClick(suggestion)}
                      sx={{ borderRadius: 1 }}
                    >
                      <SearchIcon sx={{ mr: 2, color: 'text.secondary', fontSize: 18 }} />
                      <ListItemText 
                        primary={suggestion}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          </Paper>
        )}
      </Box>
    </ClickAwayListener>
  );
};

export default SearchComponent;